import AxiosInstance from '@/api/axios';
import Button from '@/components/Base/Button';
import { FormTextarea } from '@/components/Base/Form';
import Lucide from '@/components/Base/Lucide';
import { toast } from '@/components/Base/Toast/ToastManager';
import encryptDecrypt from '@/utils/encryptDecrypt';
import { splitClientPath } from '@/utils/nonClientRoutes';
import clsx from 'clsx';
import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Asks every signed-in user how they find the new UI.
 *
 * A card that slides in at the top centre rather than a modal, so it is
 * noticeable without blocking the page. Shown once
 * the user has had time to settle in, and the server decides whether to ask at
 * all: never again after a submission, a 7-day snooze after "Maybe later", and
 * not after the third "Maybe later" (see uiFeedback.service.ts).
 */

/** Let the page load and the user get going before asking. */
const SHOW_DELAY_MS = 45_000;
/** Checked once per browser session, so navigation does not re-fetch. */
const SESSION_KEY = 'uiFeedbackPromptChecked';
const MAX_COMMENT = 2000;
/** Matches the exit animation below. */
const ANIMATION_MS = 250;

// Keyframes rather than a transition: an enter transition needs the hidden
// state painted first, which React does not guarantee; an animation plays on
// mount regardless.
const ANIMATION_CSS = `
@keyframes ui-feedback-in {
  0% { opacity: 0; transform: translate(-50%, calc(-100% - 20px)); }
  60% { opacity: 1; transform: translate(-50%, 10px); }
  80% { transform: translate(-50%, -4px); }
  100% { opacity: 1; transform: translate(-50%, 0); }
}
@keyframes ui-feedback-out {
  from { opacity: 1; transform: translate(-50%, 0); }
  to { opacity: 0; transform: translate(-50%, calc(-100% - 20px)); }
}
/* A 0.6s wiggle at the start of each 3s cycle, then still until the next. */
@keyframes ui-feedback-wiggle {
  0%, 20%, 100% { transform: rotate(0); }
  4% { transform: rotate(-12deg) scale(1.15); }
  8% { transform: rotate(10deg) scale(1.15); }
  12% { transform: rotate(-6deg); }
  16% { transform: rotate(4deg); }
}
.ui-feedback-in { animation: ui-feedback-in 600ms ease-out both; }
.ui-feedback-out { animation: ui-feedback-out ${ANIMATION_MS}ms ease-in both; }
/* After the card lands, the emojis keep wiggling in turn until one is picked. */
.ui-feedback-emoji { display: inline-block; }
.ui-feedback-emoji-wiggle { animation: ui-feedback-wiggle 3s ease-in-out infinite both; }
@media (prefers-reduced-motion: reduce) {
  .ui-feedback-in, .ui-feedback-out, .ui-feedback-emoji-wiggle { animation: none; }
}
`;

const RATINGS: { value: number; emoji: string; label: string }[] = [
  { value: 1, emoji: '😞', label: 'Very poor' },
  { value: 2, emoji: '🙁', label: 'Poor' },
  { value: 3, emoji: '😐', label: 'Okay' },
  { value: 4, emoji: '🙂', label: 'Good' },
  { value: 5, emoji: '😃', label: 'Excellent' },
];

type Step = 'rate' | 'comment' | 'thanks';

const readSessionFlag = (): boolean => {
  try {
    return sessionStorage.getItem(SESSION_KEY) === '1';
  } catch {
    return false;
  }
};

const writeSessionFlag = () => {
  try {
    sessionStorage.setItem(SESSION_KEY, '1');
  } catch {
    // Storage unavailable: the server-side snooze still applies.
  }
};

/** The page without its encrypted client segment, plus the client it names. */
const describeLocation = (pathname: string) => {
  const { encryptedClientId, page } = splitClientPath(pathname);
  const client = encryptedClientId
    ? encryptDecrypt.decryptClientId(encryptedClientId) || undefined
    : undefined;
  return { page, client };
};

const UiFeedbackPrompt = () => {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  // False while the exit animation plays, before `open` unmounts the card.
  const [visible, setVisible] = useState(true);
  const [step, setStep] = useState<Step>('rate');
  const [rating, setRating] = useState<number | null>(null);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (readSessionFlag()) return;

    // A background tab does not play animations, so the card would already be
    // sitting still when the user came back. Wait until they can see it.
    const show = () => {
      setVisible(true);
      setOpen(true);
    };
    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      document.removeEventListener('visibilitychange', onVisibilityChange);
      show();
    };
    const showWhenTabVisible = () => {
      if (document.visibilityState === 'visible') show();
      else document.addEventListener('visibilitychange', onVisibilityChange);
    };

    const timer = window.setTimeout(async () => {
      writeSessionFlag();
      try {
        const res = await AxiosInstance.get('/ui-feedback/status');
        if (res.data?.data?.shouldShow) showWhenTabVisible();
      } catch {
        // Never surface an error for an unprompted pop-up.
      }
    }, SHOW_DELAY_MS);

    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  const hide = useCallback(() => {
    setVisible(false);
    window.setTimeout(() => setOpen(false), ANIMATION_MS);
  }, []);

  const dismiss = useCallback(async () => {
    hide();
    if (step === 'thanks') return;
    try {
      await AxiosInstance.post(
        '/ui-feedback/dismiss',
        describeLocation(location.pathname)
      );
    } catch {
      // A failed dismissal only means we may ask again next session.
    }
  }, [hide, step, location.pathname]);

  const submit = useCallback(async () => {
    if (!rating || submitting) return;
    setSubmitting(true);
    try {
      await AxiosInstance.post('/ui-feedback', {
        rating,
        comment: comment.trim() || undefined,
        ...describeLocation(location.pathname),
      });
      setStep('thanks');
      window.setTimeout(hide, 2500);
    } catch (err: any) {
      toast.show({
        title: 'Error',
        content: err?.response?.data?.message ?? 'Failed to submit feedback',
        type: 'warning',
        duration: 2000,
      });
    } finally {
      setSubmitting(false);
    }
  }, [hide, rating, comment, submitting, location.pathname]);

  if (!open) return null;

  return (
    <div
      role='dialog'
      aria-labelledby='ui-feedback-title'
      // left-1/2 plus translate(-50%) in the keyframes centres the card.
      className={clsx(
        'fixed left-1/2 top-5 z-[60] w-[calc(100vw-2.5rem)] max-w-md -translate-x-1/2 rounded-xl border border-slate-200 bg-white p-5 shadow-2xl dark:border-darkmode-400 dark:bg-darkmode-600',
        visible ? 'ui-feedback-in' : 'ui-feedback-out'
      )}
    >
      <style>{ANIMATION_CSS}</style>
      <button
        type='button'
        aria-label='Close'
        onClick={dismiss}
        className='absolute right-3 top-3 rounded-full p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-darkmode-400'
      >
        <Lucide icon='X' className='h-4 w-4' />
      </button>

      {step === 'thanks' ? (
        <div className='py-2 pr-6'>
          <div className='font-medium text-slate-800 dark:text-slate-100'>
            🎉 Thanks for your feedback!
          </div>
          <div className='mt-1 text-sm text-slate-500'>
            It goes straight to our product team.
          </div>
        </div>
      ) : (
        <>
          <div
            id='ui-feedback-title'
            className='pr-6 font-medium text-slate-800 dark:text-slate-100'
          >
            How are you finding the new look?
          </div>
          <div className='mt-1 text-sm text-slate-500'>
            We'd love to hear how the new interface is working for you.
          </div>

          <div className='mt-4 flex justify-between gap-1'>
            {RATINGS.map(({ value, emoji, label }, index) => (
              <button
                key={value}
                type='button'
                title={label}
                aria-label={label}
                aria-pressed={rating === value}
                onClick={() => {
                  setRating(value);
                  setStep('comment');
                }}
                className={clsx(
                  'flex flex-1 flex-col items-center rounded-lg border px-1 py-2 text-2xl leading-none transition-all hover:scale-105',
                  rating === value
                    ? 'border-primary bg-primary/10'
                    : 'border-transparent hover:bg-slate-100 dark:hover:bg-darkmode-400'
                )}
              >
                <span
                  className={clsx(
                    'ui-feedback-emoji',
                    step === 'rate' && 'ui-feedback-emoji-wiggle'
                  )}
                  // Starts once the card has landed, then ripples left to right.
                  style={{ animationDelay: `${600 + index * 90}ms` }}
                >
                  {emoji}
                </span>
                <span className='mt-1 text-[10px] text-slate-500'>{label}</span>
              </button>
            ))}
          </div>

          {step === 'comment' && (
            <div className='mt-4'>
              <FormTextarea
                rows={3}
                maxLength={MAX_COMMENT}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder={
                  rating && rating <= 3
                    ? 'What could we improve? (optional)'
                    : 'What do you like most? (optional)'
                }
                className='text-sm'
              />
            </div>
          )}

          <div className='mt-4 flex items-center justify-end gap-2'>
            <Button
              variant='outline-secondary'
              type='button'
              className='text-sm'
              onClick={dismiss}
            >
              Maybe later
            </Button>
            {step === 'comment' && (
              <Button
                variant='primary'
                type='button'
                className='text-sm'
                disabled={!rating || submitting}
                onClick={submit}
              >
                {submitting ? 'Sending…' : 'Send feedback'}
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default UiFeedbackPrompt;
